import React, { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import clsx from 'clsx';

import { homeOwnershipsApi } from 'prequalificationV2/api/HomeOwnershipsApi';
import {
  getErrorMessageForRequiredFields,
  getStreetNameFromStreetAddress,
  getStreetNumberFromStreetAddress,
} from 'prequalificationV2/utils/stringUtils';
import { getMessageForInvalidFields } from 'prequalificationV2/utils/validationUtils';
import { isMonthYearNotBeforeMinYear, isValidDateBeforeNow } from 'prequalificationV2/utils/dateUtils';
import { DateFormat } from 'enums/DateFormat';
import NumberInput from 'components/Inputs/NumberInput';
import { SearchLocationInput } from 'components/SearchLocationInput';
import Select from 'components/Select';
import { CurrentOwnershipStatus } from 'enums/ApplicationsVariables';
import { getOptions, OptionType } from 'prequalificationV2/utils/inputSelect';
import { ZIP_CODE_MAX_LENGTH } from 'prequalificationV2/utils/constants';
import {
  HomeOwnershipInformationLabel,
  OwnershipLabel,
} from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/HomeOwnershipTab/HomeOwnershipInfo/homeOwnershipInfo.enums';
import { HomeOwnershipInformationFormValues } from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/HomeOwnershipTab/HomeOwnershipInfo/homeOwnershipInfo.types';
import * as notificationManager from 'notifications';
import { FailureNotificationType } from 'notifications';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import {
  getAddressCaptionMessage,
  removeIncorrectSymbols,
  validateCityField,
} from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/HomeOwnershipTab/HomeOwnershipInfo/homeOwnershipInfo.utils';
import { setLoanPortal } from 'prequalificationV2/actions/dashboardPortal';
import { Button } from 'components/Buttons/Button';
import {
  MAP_OWNERSHIP_LABEL_TO_OWNERSHIP,
  MAP_OWNERSHIP_TO_OWNERSHIP_LABEL,
} from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/HomeOwnershipTab/HomeOwnershipInfo/homeOwnershipInfo.constants';
import Loader from 'components/Loader';
import styles from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/HomeOwnershipTab/HomeOwnershipInfo/HomeOwnershipInfo.module.scss';
import { getDateFromFormat, removeNumberOfYears } from 'utils/dateUtils';

const HomeOwnershipInfo = () => {
  const dispatch = useAppDispatch();
  const application = useAppSelector((state) => state.application);
  const variables = application.variables!;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { isHomeOwnershipInformationComplete } = useAppSelector((state) => state.dashboardPortal);
  const [isPreviousAddressFormOpened, setIsPreviousAddressFormOpened] = useState(false);
  const YEAR_DIFFERENCE_FOR_PREVIOUS_ADDRESS = 2;

  const {
    register,
    unregister,
    formState: { errors },
    setValue,
    trigger,
    watch,
    control,
    formState,
    getValues,
    handleSubmit,
    clearErrors,
  } = useForm<HomeOwnershipInformationFormValues>({
    defaultValues: {
      moveInDate: variables.move_in_date || '',
      ownershipStatus: MAP_OWNERSHIP_TO_OWNERSHIP_LABEL[variables.current_home_ownership!] || '',
      previousAddressStreetAddress: variables.borrower_previous_address_1?.street_name || '',
      previousAddressCity: variables.borrower_previous_address_1?.city || '',
      previousAddressState: variables.borrower_previous_address_1?.state_or_province || '',
      previousAddressZip: variables.borrower_previous_address_1?.zip_or_postal_code || '',
      previousMoveInDate: variables.borrower_previous_move_in_date_1 || '',
    },
  });
  const watcher = watch();

  useEffect(() => {
    register('moveInDate', {
      required: getErrorMessageForRequiredFields(HomeOwnershipInformationLabel.MoveInDate),
      validate: (value) =>
        (isValidDateBeforeNow(value, DateFormat.MonthYear) && isMonthYearNotBeforeMinYear(value)) ||
        'Please enter a valid date',
    });
    register('ownershipStatus', {
      required: getErrorMessageForRequiredFields(HomeOwnershipInformationLabel.OwnershipStatus),
    });
  }, [register, watcher]);

  useEffect(() => {
    if (
      isValidDateBeforeNow(watcher.moveInDate, DateFormat.MonthYear) &&
      getDateFromFormat(watcher.moveInDate, DateFormat.MonthYear) >
        removeNumberOfYears(YEAR_DIFFERENCE_FOR_PREVIOUS_ADDRESS)
    ) {
      register('previousAddressStreetAddress', {
        required: getErrorMessageForRequiredFields(HomeOwnershipInformationLabel.PreviousStreetAddress),
      });
      register('previousAddressCity', {
        required: getErrorMessageForRequiredFields(HomeOwnershipInformationLabel.PreviousCity),
        validate: (value) => {
          return validateCityField(value, HomeOwnershipInformationLabel.PreviousCity);
        },
      });
      register('previousAddressState', {
        required: getErrorMessageForRequiredFields(HomeOwnershipInformationLabel.PreviousState),
      });
      register('previousAddressZip', {
        required: getErrorMessageForRequiredFields(HomeOwnershipInformationLabel.PreviousZip),
        validate: (value) => {
          const zipValue = value?.trim().replace(/\D/g, '');

          return (
            zipValue?.length === ZIP_CODE_MAX_LENGTH ||
            getMessageForInvalidFields(HomeOwnershipInformationLabel.PreviousZip)
          );
        },
      });
      register('previousMoveInDate', {
        required: getErrorMessageForRequiredFields(HomeOwnershipInformationLabel.PreviousMoveInDate),
        validate: (value) => {
          if (!isValidDateBeforeNow(value, DateFormat.MonthYear) || !isMonthYearNotBeforeMinYear(value)) {
            return 'Please enter a valid date';
          }

          return (
            getDateFromFormat(value, DateFormat.MonthYear) <=
              getDateFromFormat(watcher.moveInDate, DateFormat.MonthYear) ||
            `${HomeOwnershipInformationLabel.PreviousMoveInDate} cannot be later than ${HomeOwnershipInformationLabel.MoveInDate}`
          );
        },
      });

      setIsPreviousAddressFormOpened(true);

      if (watcher.previousMoveInDate) {
        trigger('previousMoveInDate');
      }
    } else {
      unregister([
        'previousAddressStreetAddress',
        'previousAddressCity',
        'previousAddressState',
        'previousAddressZip',
        'previousMoveInDate',
      ]);

      setIsPreviousAddressFormOpened(false);
    }
  }, [watcher.moveInDate]);

  const onSubmit = handleSubmit(async () => {
    try {
      setIsLoading(true);

      await homeOwnershipsApi.updateHomeOwnership({
        applicationId: application.applicationId!,
        moveInDate: watcher.moveInDate,
        ownershipStatus: MAP_OWNERSHIP_LABEL_TO_OWNERSHIP[watcher.ownershipStatus!],
        ...(isPreviousAddressFormOpened && {
          previousAddress: {
            streetName: getStreetNameFromStreetAddress(watcher.previousAddressStreetAddress),
            streetNumber: getStreetNumberFromStreetAddress(watcher.previousAddressStreetAddress),
            city: watcher.previousAddressCity,
            stateOrProvince: watcher.previousAddressState,
            zipOrPostalCode: watcher.previousAddressZip,
          },
          previousMoveInDate: watcher.previousMoveInDate,
        }),
      });

      dispatch(
        setLoanPortal({
          isHomeOwnershipInformationComplete: true,
        }),
      );
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <>
      {isLoading ? (
        <Loader customContainerClassname={styles.loader} />
      ) : (
        <form id="form" onSubmit={onSubmit}>
          <div className={styles.rowContainer}>
            <NumberInput
              label={HomeOwnershipInformationLabel.MoveInDate}
              placeholder="MM/YYYY"
              errorMessage={errors.moveInDate?.message}
              className={clsx(styles.inputDateContainer, styles.inputDateContainer)}
              name="moveInDate"
              onChange={(event) => {
                clearErrors('moveInDate');
                setValue('moveInDate', event.target.value);
              }}
              onBlur={() => {
                if (watcher.moveInDate) {
                  trigger('moveInDate');
                }
              }}
              format="##/####"
              value={watcher.moveInDate}
              mask="_"
              allowEmptyFormatting
              disabled={isHomeOwnershipInformationComplete}
            />
            <div className={styles.formInput}>
              <Select
                useFormControl
                required
                control={control}
                onChange={(event: any) => {
                  setValue('ownershipStatus', event.value as OwnershipLabel);
                  trigger('ownershipStatus');
                }}
                name="ownershipStatus"
                label={HomeOwnershipInformationLabel.OwnershipStatus}
                options={getOptions(CurrentOwnershipStatus)}
                value={getOptions(OwnershipLabel).find(
                  (option: OptionType) => watcher.ownershipStatus === option.value,
                )}
                errorMessage={errors.ownershipStatus?.message}
                isDisabled={isHomeOwnershipInformationComplete}
              />
            </div>
          </div>

          {isPreviousAddressFormOpened && (
            <div className={styles.previousAddressContainer}>
              <div className={styles.addressHeader}>Previous Address</div>
              <SearchLocationInput
                containerClassName={styles.previousAddressSearchContainer}
                control={control}
                required
                useFormControl
                inputNames={{
                  streetAddress: 'previousAddressStreetAddress',
                  city: 'previousAddressCity',
                  state: 'previousAddressState',
                  zip: 'previousAddressZip',
                }}
                errors={{
                  streetAddress: errors.previousAddressStreetAddress,
                  city: errors.previousAddressCity,
                  state: errors.previousAddressState,
                  zip: errors.previousAddressZip,
                }}
                streetLabel={HomeOwnershipInformationLabel.PreviousStreetAddress}
                cityLabel={HomeOwnershipInformationLabel.PreviousCity}
                stateLabel={HomeOwnershipInformationLabel.PreviousState}
                zipLabel={HomeOwnershipInformationLabel.PreviousZip}
                caption={getAddressCaptionMessage(HomeOwnershipInformationLabel.PreviousStreetAddress)}
                initialValue={{
                  streetAddress: watcher.previousAddressStreetAddress || '',
                  city: watcher.previousAddressCity || '',
                  state: watcher.previousAddressState || '',
                  zip: watcher.previousAddressZip || '',
                }}
                inputChange={(name, value) => {
                  clearErrors(name as keyof HomeOwnershipInformationFormValues);

                  if (name === 'previousAddressStreetAddress') {
                    setValue(name as keyof HomeOwnershipInformationFormValues, removeIncorrectSymbols(value));
                  } else {
                    setValue(name as keyof HomeOwnershipInformationFormValues, value);
                  }

                  if (formState.isSubmitted) {
                    trigger(name as keyof HomeOwnershipInformationFormValues);
                  }
                }}
                getValues={getValues}
                disabled={isHomeOwnershipInformationComplete}
              />
              <div className={styles.rowContainer}>
                <NumberInput
                  label={HomeOwnershipInformationLabel.PreviousMoveInDate}
                  placeholder="MM/YYYY"
                  errorMessage={errors.previousMoveInDate?.message}
                  className={styles.formInput}
                  name="previousMoveInDate"
                  onChange={(event) => {
                    clearErrors('previousMoveInDate');
                    setValue('previousMoveInDate', event.target.value);
                  }}
                  onBlur={() => {
                    if (watcher.previousMoveInDate) {
                      trigger('previousMoveInDate');
                    }
                  }}
                  format="##/####"
                  value={watcher.previousMoveInDate}
                  mask="_"
                  allowEmptyFormatting
                  disabled={isHomeOwnershipInformationComplete}
                />
              </div>
            </div>
          )}
          {!isHomeOwnershipInformationComplete && (
            <Button className={styles.submitButton} onClick={onSubmit}>
              Save
            </Button>
          )}
        </form>
      )}
    </>
  );
};

export default HomeOwnershipInfo;
